import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';

import { AudienceDao } from './audience-dao';
import { AudienceSetDetail } from './audience-set-detail';
import { CombinedListStyle } from './styles/combined-list-styles';

import {
  AudienceSetOperator,
  AudienceType,
  MaxAudienceSets,
  MaxAudiencesInAudienceSet,
  NewMaxAudienceSetsExceedingError,
  NewMaxAudiencesExceedingError,
  NewSimilarAudienceOperatorError,
  NewSimilarAudienceSetError,
  NewCustomerListCombinationError,
  NewEmptyAudienceSetError,
  NewOnlyNotAudienceSetError,
  NewNoAudienceSetError,
  NewCombinedListEditNotByCreatorError,
  NewAudienceIdInvalidError,
  NewInternalError,
  NewAudienceInConfictSetError,
  NewEnhancedCustomerListError,
} from './constants';

export class CombinedListDetail extends React.PureComponent {
  constructor(props) {
    super(props);
    this.logMessage = props.logMessage;
    this.audienceDao = new AudienceDao({
      loadAudiencesAsync: this.props.loadAudiencesAsync,
    });

    const newAudienceSet = {
      id: -1,
      isEditing: true,
      operator: AudienceSetOperator.And,
      audiences: [],
    };

    this.state = {
      isLoadingAudience: false,

      audienceSets: [],
      audienceSummary: [],
      isAddingNewAudienceSet: !props.isEditMode,
      newAudienceSet: props.isEditMode ? null : newAudienceSet,

      error: null,
    };

    this.loadAudiencePromise = null;

    // only for test purpose
    if (this.props.testRef) {
      this.props.testRef.current = {
        beforeSavingValidation: this.beforeSavingValidation.bind(this),
        convertCombinedListToMTFormat: this.convertCombinedListToMTFormat.bind(this),
        backFillMTResponse: this.backFillMTResponse.bind(this),
        audienceDao: this.audienceDao,
        serialize: this.serialize.bind(this),
        load: this.load.bind(this),
        convertCombinedListToUIFormat: this.convertCombinedListToUIFormat.bind(this),
        simpleValidateAudienceSet: this.simpleValidateAudienceSet.bind(this),
        fullValidateAudienceSet: this.fullValidateAudienceSet.bind(this),
        validateCombinedList: this.validateCombinedList.bind(this),
        deltaCalculateDisabledAudienceTypes: this.deltaCalculateDisabledAudienceTypes.bind(this),
      }
    }
  }

  componentDidMount() {
    // If it's in Editing, load audience data aggresively
    if (this.props.isEditMode) {
      this.activate();
    }
    this.logMessage(false, 'CombinedListDetail.componentDidMount', 'Combined List Detail React did mount');
  }

  componentDidUpdate() {
    this.logMessage(false, 'CombinedListDetail.componentDidUpdate', 'Combined List Detail React did update');
  }

  componentDidCatch(error, info) {
    this.logMessage(true, 'CombinedListDetail.componentDidCatch', `Combined List Detail React did catch error: ${error} ${info.componentStack}`);
  }

  componentWillUnmount() {
    this.logMessage(false, 'CombinedListDetail.componentWillUnmount', 'Combined List Detail React will unmount');
  }

  // Audience set operation
  onEditAudienceSet = (index) => {
    this.setState({ error: null }); // Bad impl for disappearing

    const newAudienceSets = _.map(
      this.state.audienceSets,
      (audienceSet, idx) =>
        (idx === index ? _.defaults({ isEditing: true }, audienceSet) : audienceSet)
    );

    this.setState({ audienceSets: newAudienceSets });
    this.logMessage(false, 'CombinedListDetail.onEditAudienceSet', 'Combined List Detail React onEditAudienceSet');
  };

  onSaveExistingAudienceSet = (operator, audiences, index) => {
    this.setState({ error: null }); // Bad impl for disappearing

    const updatedAudienceSet = {
      isEditing: false,
      operator,
      audiences,
    };

    const otherAudienceSets = _.compact(_.map(
      this.state.audienceSets,
      (x, idx) => (idx === index ? null : x)
    ));

    const error = this.fullValidateAudienceSet(updatedAudienceSet, otherAudienceSets);

    if (!error) {
      const updatedAudienceSets = _.map(
        this.state.audienceSets,
        (x, idx) => (idx === index ? updatedAudienceSet : x)
      );
      const audienceSummary = this.summarizeAudienceSets(updatedAudienceSets);

      this.setState({ audienceSets: updatedAudienceSets, audienceSummary });
    }
    this.logMessage(false, 'CombinedListDetail.onSaveExistingAudienceSet', 'Combined List Detail React onSaveExistingAudienceSet');
    return error;
  };

  onCancelExistingAudienceSet = (index) => {
    this.setState({ error: null }); // Bad impl for disappearing

    const newAudienceSets = _.map(
      this.state.audienceSets,
      (audienceSet, idx) =>
        (idx === index ? _.defaults({ isEditing: false }, audienceSet) : audienceSet)
    );
    this.logMessage(false, 'CombinedListDetail.onCancelExistingAudienceSet', 'Combined List Detail React onCancelExistingAudienceSet');
    this.setState({ audienceSets: newAudienceSets });
  };

  onRemoveAudienceSet = (index) => {
    this.setState({ error: null }); // Bad impl for disappearing error

    const newCombinedList = _.compact(_.map(
      this.state.audienceSets,
      (audienceSet, idx) => (idx === index ? null : audienceSet)
    ));

    const audienceSummary = this.summarizeAudienceSets(newCombinedList);
    this.logMessage(false, 'CombinedListDetail.onRemoveAudienceSet', 'Combined List Detail React onRemoveAudienceSet');
    this.setState({ audienceSets: newCombinedList, audienceSummary });
  };

  onAddNewAudienceSet = () => {
    this.setState({ error: null }); // Bad impl for disappearing error

    const newAudienceSet = {
      id: -1,
      isEditing: true,
      operator: AudienceSetOperator.And,
      audiences: [],
    };
    this.logMessage(false, 'CombinedListDetail.onAddNewAudienceSet', 'Combined List Detail React onAddNewAudienceSet');
    this.setState({ isAddingNewAudienceSet: true, newAudienceSet });
  };

  onSaveNewAudienceSet = (operator, audiences) => {
    this.setState({ error: null }); // Bad impl for disappearing

    const newAudienceSet = {
      id: _.uniqueId(),
      isEditing: false,
      operator,
      audiences,
    };

    const error = this.fullValidateAudienceSet(newAudienceSet, this.state.audienceSets);

    if (!error) {
      const newAudienceSets = [...this.state.audienceSets, newAudienceSet];
      const audienceSummary = this.summarizeAudienceSets(newAudienceSets);

      this.setState({
        isAddingNewAudienceSet: false,
        audienceSets: newAudienceSets,
        audienceSummary,
      });
    }
    this.logMessage(false, 'CombinedListDetail.onSaveNewAudienceSet', 'Combined List Detail React onSaveNewAudienceSet');
    return error;
  };

  onCancelNewAudienceSet = () => {
    this.setState({ error: null }); // Bad impl for disappearing

    this.setState({
      isAddingNewAudienceSet: false,
      newAudienceSet: null,
    });
    this.logMessage(false, 'CombinedListDetail.onCancelNewAudienceSet', 'Combined List Detail React onCancelNewAudienceSet');
  };

  // Data format converter
  convertCombinedListToMTFormat = (audienceSets) => {
    const mtFormatAudienceSetsObject = _.map(
      audienceSets,
      (audienceSet) => {
        const operator = audienceSet.operator.value;
        const audienceIds = _.map(audienceSet.audiences, x => x.Id);

        return {
          '@odata.type': '#Model.AudienceSet',
          Operator: operator,
          AudienceIds: audienceIds,
        };
      }
    );

    return mtFormatAudienceSetsObject;
  };

  convertCombinedListToUIFormat = (mtCombinedList, audienceDao) => {
    const uiFormatCombinedList = _.map(
      mtCombinedList,
      (mtAudienceSet) => {
        const operator = _.find(
          _.values(AudienceSetOperator),
          x => x.value === mtAudienceSet.Operator
        );

        const audiences = _.compact(_.map(
          mtAudienceSet.AudienceIds,
          (id) => {
            const audience = audienceDao.getAudienceById(id);
            if (!audience) {
              return {
                Id: id,
                Name: this.props.i18n.getString('Unavailable_Audience_withId', {
                  Id: id,
                }),
                NotAvailable: true,
                Type: {
                  value: 'NotAvailable',
                },
              };
            }
            return audience;
          }
        ));

        return {
          operator,
          audiences,
          isEditing: false,
        };
      }
    );

    return uiFormatCombinedList;
  };

  mapMTResponseErrorcode = (mtErrorCode) => {
    switch (mtErrorCode) {
      case 'AudienceSetsIsEmpty':
        return NewNoAudienceSetError(this.props.i18n);
      case 'AudienceSetsIsTooLarge':
        return NewMaxAudienceSetsExceedingError(this.props.i18n);
      case 'NoAudienceSelected':
        return NewEmptyAudienceSetError(this.props.i18n);
      case 'AllNOTOperatorIsNotAllowed':
        return NewOnlyNotAudienceSetError(this.props.i18n);
      case 'SimilarAudienceCanOnlyBeInSingleORSet':
        return NewSimilarAudienceSetError(this.props.i18n);
      case 'CustomerListsCanOnlyBeCombinedWithOtherCustomerLists':
        return NewCustomerListCombinationError(this.props.i18n);
      case 'CombinedListCanOnlyBeEditedByCreator':
        return NewCombinedListEditNotByCreatorError(this.props.i18n);
      case 'InvalidAudienceId':
        return NewAudienceIdInvalidError(this.props.i18n);
      case 'InvalidAudienceSetOperator':
      case 'AudienceTypeIsNotSupportedForANDOperator':
      case 'AudienceTypeIsNotSupportedForOROperator':
      case 'AudienceTypeIsNotSupportedForNOTOperator':
      case 'CustomerNotEligibleForAudienceCombination':
        // This will only happen by hacking or system issue
        return NewInternalError(this.props.i18n);
      default:
        return null;
    }
  };

  // Validate functions
  statAudiences = (audiences) => {
    let similarAudienceSize = 0;
    let customerListAudienceSize = 0;
    let customerAudienceSize = 0;
    let remarketAudienceSize = 0;
    let productAudienceSize = 0;

    _.each(audiences, (audience) => {
      switch (audience.Type) {
        case AudienceType.RemarketingSimilar:
          similarAudienceSize += 1;
          break;

        case AudienceType.Custom:
          customerAudienceSize += 1;
          break;

        case AudienceType.CustomerList:
          customerListAudienceSize += 1;
          break;

        case AudienceType.RemarketingList:
          remarketAudienceSize += 1;
          break;

        case AudienceType.Product:
          productAudienceSize += 1;
          break;

        default:
      }
    });

    return {
      similarAudienceSize,
      customerAudienceSize,
      customerListAudienceSize,
      ordinaryAudienceSize: remarketAudienceSize + productAudienceSize, // Remarketing and Product audiences are ordinary audiences
      remarketAudienceSize,
      productAudienceSize,
    };
  };

  simpleValidateAudienceSet = (operator, audiences) => {
    // Do a quick validation within the set everytime a change
    if (audiences.length > MaxAudiencesInAudienceSet) {
      return NewMaxAudiencesExceedingError(this.props.i18n);
    }

    const {
      similarAudienceSize, customerAudienceSize, customerListAudienceSize,
    } = this.statAudiences(audiences);

    if (similarAudienceSize > 0 && operator !== AudienceSetOperator.Or) {
      return NewSimilarAudienceOperatorError(this.props.i18n);
    }

    if (similarAudienceSize > 0 &&
      (customerListAudienceSize > 0 && customerAudienceSize > 0)) {
      return NewEnhancedCustomerListError(this.props.newI18n);
    }


    return null;
  };

  fullValidateAudienceSet = (audienceSetToUpdate, restAudienceSets) => {
    // Within single set
    if (audienceSetToUpdate.audiences.length === 0) {
      return NewEmptyAudienceSetError(this.props.i18n);
    }

    if (audienceSetToUpdate.audiences.length > MaxAudiencesInAudienceSet) {
      return NewMaxAudiencesExceedingError(this.props.i18n);
    }

    const {
      similarAudienceSize, customerAudienceSize, customerListAudienceSize,
    }
      = this.statAudiences(audienceSetToUpdate.audiences);


    if (similarAudienceSize > 0 && audienceSetToUpdate.operator !== AudienceSetOperator.Or) {
      return NewSimilarAudienceOperatorError(this.props.i18n);
    }

    const simpleCheckError =
      this.simpleValidateAudienceSet(audienceSetToUpdate.operator, audienceSetToUpdate.audiences);
    if (!_.isNull(simpleCheckError)) {
      return simpleCheckError;
    }

    // Within all sets
    if (restAudienceSets.length + 1 > MaxAudienceSets) {
      return NewMaxAudienceSetsExceedingError(this.props.i18n);
    }

    let othersSimilarAudienceSize = 0;
    let othersCustomerListAudienceSize = 0;
    let othersCustomerAudienceSize = 0;

    _.each(
      restAudienceSets,
      (audienceSet) => {
        const {
          similarAudienceSize: similarSize,
          customerAudienceSize: customerSize,
          customerListAudienceSize: customerListSize,
        } = this.statAudiences(audienceSet.audiences);
        othersSimilarAudienceSize += similarSize;
        othersCustomerListAudienceSize += customerListSize;
        othersCustomerAudienceSize += customerSize;
      }
    );

    if (othersSimilarAudienceSize > 0 && similarAudienceSize > 0) {
      return NewSimilarAudienceSetError(this.props.i18n);
    }

    if (othersSimilarAudienceSize > 0 && (customerListAudienceSize > 0 || customerAudienceSize > 0)) {
      return NewEnhancedCustomerListError(this.props.newI18n);
    }

    if (similarAudienceSize > 0 && (othersCustomerAudienceSize > 0 || othersCustomerListAudienceSize > 0)) {
      return NewEnhancedCustomerListError(this.props.newI18n);
    }

    // check if same audience is included in both AND set and NOT set
    if ((audienceSetToUpdate.operator.value === AudienceSetOperator.And.value ||
      audienceSetToUpdate.operator.value === AudienceSetOperator.Not.value) &&
      restAudienceSets.length > 0) {
      const conflictAudiences = [];
      _.each(audienceSetToUpdate.audiences, (audience) => {
        if (this.isAudienceInConflictSet(
          audienceSetToUpdate.operator.value,
          audience.Id,
          restAudienceSets
        )) {
          conflictAudiences.push(audience);
        }
      });
      if (!_.isEmpty(conflictAudiences)) {
        return NewAudienceInConfictSetError(this.props.i18n, conflictAudiences);
      }
    }

    return null;
  };

  isAudienceInConflictSet = (setOperator, audienceId, otherAudienceSets) => {
    const oppositeSetOperator = setOperator === AudienceSetOperator.And.value ?
      AudienceSetOperator.Not.value : AudienceSetOperator.And.value;
    const oppositeAudienceSets = _.filter(
      otherAudienceSets,
      set => set.operator.value === oppositeSetOperator
    );
    const oppositeAudiences = _.reduce(
      oppositeAudienceSets,
      (total, set) => _.union(total, set.audiences),
      []
    );

    if (_.isEmpty(oppositeAudienceSets) || _.isEmpty(oppositeAudiences)) {
      return false;
    }

    const result = _.find(oppositeAudiences, audience => audience.Id === audienceId);

    return result !== undefined;
  };

  beforeSavingValidation = (audienceSets) => {
    // When save combined list, the audience sets in the status are good
    // but only one problem that we will not validate only "NOT" sets before saving
    let audienceSetWithNotOperatorSize = 0;
    let audienceSetWithOtherOperatorSize = 0;

    _.each(
      audienceSets,
      (audienceSet) => {
        switch (audienceSet.operator) {
          case AudienceSetOperator.Not:
            audienceSetWithNotOperatorSize += 1;
            break;
          default:
            audienceSetWithOtherOperatorSize += 1;
        }
      }
    );

    if (audienceSetWithOtherOperatorSize === 0) {
      return audienceSetWithNotOperatorSize === 0 ?
        NewNoAudienceSetError(this.props.i18n) : NewOnlyNotAudienceSetError(this.props.i18n);
    }

    return null;
  };

  deltaCalculateDisabledAudienceTypes = (operator, audiences, index = null) => {
    const disabledAudienceTypes = [];

    let {
      similarAudienceSize,
      customerAudienceSize,
      customerListAudienceSize,
    } = this.statAudiences(audiences);

    _.each(this.state.audienceSummary, (x, idx) => {
      if (index === idx) {
        return;
      }

      similarAudienceSize += x.similarAudienceSize;
      customerAudienceSize += x.customerAudienceSize;
      customerListAudienceSize += x.customerListAudienceSize;
    });

    // If other has Similar, no similar audiences are permitted in the new set
    if (operator !== AudienceSetOperator.Or) {
      disabledAudienceTypes.push(AudienceType.RemarketingSimilar);
    }

    if (similarAudienceSize > 0) {
      disabledAudienceTypes.push(AudienceType.CustomerList);
      disabledAudienceTypes.push(AudienceType.Custom);
    }

    if (customerListAudienceSize + customerAudienceSize > 0) {
      disabledAudienceTypes.push(AudienceType.RemarketingSimilar);
    }

    return _.uniq(disabledAudienceTypes);
  };

  deltaCalculateDisabledAudiences = (operator, audienceType, index = null) => {
    if (operator === AudienceSetOperator.Or) {
      // OR won't conflict with other sets
      return [];
    }

    const requiredOperator = (operator === AudienceSetOperator.And ?
      AudienceSetOperator.Not :
      AudienceSetOperator.And
    );

    const result = _.chain(this.state.audienceSets).map((audienceSet, idx) => {
      if (audienceSet.operator !== requiredOperator || idx === index) {
        return [];
      }

      const audienceOfRequiredType = _.filter(
        audienceSet.audiences,
        x => x.Type === audienceType
      );

      return audienceOfRequiredType;
    })
      .flatten()
      .uniq()
      .value();

    return result;
  };

  summarizeAudienceSets = audienceSets => _.map(
    audienceSets,
    x => this.statAudiences(x.audiences)
  );

  // Interface for isolation external operations
  activate() {
    if (!this.loadAudiencePromise) {
      this.setState({ isLoadingAudience: true });

      const loadAudiencePromise = this.audienceDao.refresh()
        .finally(() => { this.setState({ isLoadingAudience: false }); });

      this.loadAudiencePromise = loadAudiencePromise;
    }
  }

  serialize = () => this.convertCombinedListToMTFormat(this.state.audienceSets);

  load = (mtFormatAudienceSets) => {
    // This func can only be called after activate

    this.loadAudiencePromise.then(() => {
      const uiFormatAudienceSets = this.convertCombinedListToUIFormat(
        mtFormatAudienceSets,
        this.audienceDao
      );

      const audienceSummary = this.summarizeAudienceSets(uiFormatAudienceSets);

      this.setState({ audienceSets: uiFormatAudienceSets, audienceSummary });
    });
  };

  validateCombinedList = () => {
    // The audience sets are all good enough but few more to validate
    const error = this.beforeSavingValidation(this.state.audienceSets);

    if (error) {
      this.setState({ error });
      return false;
    }

    return true;
  };

  backFillMTResponse = (errorCode) => {
    const error = this.mapMTResponseErrorcode(errorCode);

    if (error) {
      this.setState({ error });
    }
  };

  // render
  renderExistingAudienceSetDetail = (audienceSet, index) => (
    <AudienceSetDetail
      key={audienceSet.id}
      i18n={this.props.i18n}
      audienceDao={this.audienceDao}
      audienceSet={audienceSet}

      onEdit={() => { this.onEditAudienceSet(index); }}
      onRemove={() => { this.onRemoveAudienceSet(index); }}
      onSave={(operator, audiences) => this.onSaveExistingAudienceSet(operator, audiences, index)}
      onCancel={() => { this.onCancelExistingAudienceSet(index); }}

      validateAudienceSet={
        (operator, audiences) => this.simpleValidateAudienceSet(operator, audiences)
      }
      getDisabledAudienceTypes={
        (operator, audiences) =>
          this.deltaCalculateDisabledAudienceTypes(operator, audiences, index)
      }
      getDisabledAudiences={
        (operator, audienceType) =>
          this.deltaCalculateDisabledAudiences(operator, audienceType, index)
      }
      externalError={null}
    />
  );


  renderNewAudienceSetDetail = audienceSet => (
    <AudienceSetDetail
      key={audienceSet.id}
      i18n={this.props.i18n}
      audienceDao={this.audienceDao}
      audienceSet={audienceSet}

      onEdit={() => { }}
      onRemove={() => { }}
      onSave={(operator, audiences) => this.onSaveNewAudienceSet(operator, audiences)}
      onCancel={() => { this.onCancelNewAudienceSet(); }}

      validateAudienceSet={
        (operator, audiences) => this.simpleValidateAudienceSet(operator, audiences)
      }
      getDisabledAudienceTypes={
        (operator, audiences) => this.deltaCalculateDisabledAudienceTypes(operator, audiences, null)
      }
      getDisabledAudiences={
        (operator, audienceType) =>
          this.deltaCalculateDisabledAudiences(operator, audienceType, null)
      }
      externalError={null}
    />
  );

  renderFaker = () => (
    <div className="combined-list-loading" style={{ width: '400px' }}>
      <UiBlocker
        blocking={this.state.isLoadingAudience}
        loadingText={this.props.i18n.getString('Loading_Audiences')}
      >
        <div className="combined-list-faker" style={{ height: '200px' }} />
      </UiBlocker>
    </div>
  );

  render() {
    if (this.state.isLoadingAudience) {
      this.logMessage(false, 'Combined list detail render', 'renderFaker');
      return this.renderFaker();
    }

    const enableNewAudienceSet = this.state.audienceSets.length < MaxAudienceSets &&
      !this.state.isAddingNewAudienceSet;

    const fillerBetweenAudienceSets = (
      <div style={{ margin: '10px', color: '#9e9e9e' }}>
        {this.props.i18n.getString('Custom_Combination_Audience_Set_Seprator_Text')}
      </div>
    );

    this.logMessage(false, 'Combined list detail render', 'render');
    return (
      <div className="combined-list-detail-panel">
        <div className="existing-audience-sets" style={CombinedListStyle.combinedListPanels}>
          {
            _.map(
              this.state.audienceSets,
              (audienceSet, index) => {
                const isFillerNeeded = (index < this.state.audienceSets.length - 1) ||
                  this.state.isAddingNewAudienceSet;

                return (
                  <div
                    className="existing-audience-set-container"
                    key={`${audienceSet.operator && audienceSet.operator.value}-${index}`}
                  >
                    {this.renderExistingAudienceSetDetail(audienceSet, index)}
                    {isFillerNeeded && fillerBetweenAudienceSets}
                  </div>
                );
              }
            )
          }
        </div>
        <div className="new-audience-set" style={CombinedListStyle.newAudienceSets}>
          {
            this.state.isAddingNewAudienceSet ?
              this.renderNewAudienceSetDetail(this.state.newAudienceSet) : null
          }
        </div>
        {
          enableNewAudienceSet &&
          <input
            type="button"
            className="btn btn-hero add-new-audience-set-button"
            onClick={this.onAddNewAudienceSet}
            style={{ marginTop: '10px' }}
            value={this.props.i18n.getString('Custom_Combination_New_Audience_Set_Button_Text')}
          />
        }
        <div className="combined-list-error" style={{ color: 'red', marginTop: '4px', marginBottom: '2px' }}>
          {this.state.error && this.state.error.message}
        </div>
      </div>
    );
  }
}

CombinedListDetail.defaultProps = {
  newI18n: {
    getString: () => 'empty',
  },
  logMessage: _.noop,
};

CombinedListDetail.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  newI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }),
  isEditMode: PropTypes.bool.isRequired,
  loadAudiencesAsync: PropTypes.func.isRequired,
  logMessage: PropTypes.func,
};
